/**
 * Capabilities Handler — USER_CAPABILITIES socket event.
 *
 * The server emits this once per connection, after auth succeeds.
 * Carries { hasBrowserStreaming: boolean } — gates browser UI visibility.
 */

import type { Socket } from "socket.io-client";
import { USER_CAPABILITIES, type UserCapabilities } from "@bash/shared";
import { useConnectionStore } from "../connectionStore";
import { useBrowserStore } from "../browserStore";

/**
 * Listens for USER_CAPABILITIES events from the server and stores them.
 * The server emits this once per connection, after auth succeeds.
 *
 * If browser streaming is disabled for this user, resets the browser store
 * to clear any stale state from a previous session or profile switch.
 */
export function attachCapabilitiesHandler(socket: Socket): () => void {
  const handler = (caps: UserCapabilities) => {
    useConnectionStore.getState().setCapabilities(caps);

    // Clear stale browser state when switching to a non-browser profile.
    // Without this, the panel stays open with a stale sessionId after a
    // profile switch, showing raw JSON errors instead of the loading state.
    if (!caps.hasBrowserStreaming) {
      useBrowserStore.getState().reset();
    }
  };
  socket.on(USER_CAPABILITIES, handler);
  return () => {
    socket.off(USER_CAPABILITIES, handler);
  };
}
